import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Skeleton
} from "@mui/material";
import { ReportAllocationTable, ReportCashflowAllocationTarget, ReportCashflowAum } from "store/types";
import { useFormatNumericHook } from "hooks";
import React from "react";

const FIRST_COLUMN_WIDTH = 200;
const DEFAULT_COLUMN_WIDTH = 150;

const firstColumnStyles = {
  position: "sticky",
  left: 0,
  backgroundColor: "white",
  zIndex: 1000,
  width: FIRST_COLUMN_WIDTH,
  minWidth: FIRST_COLUMN_WIDTH
};

const firstColumnHeaderStyles = {
  ...firstColumnStyles,
  backgroundColor: "#F6F8FC",
  zIndex: 1000
};

interface Props {
  allocationTargetData: ReportCashflowAllocationTarget[] | null;
  aumData: ReportCashflowAum | null;
  allocationTableData: ReportAllocationTable | null;
  allocationTableLoading: boolean;
}
const LoadingSkeleton = () => {
  const rowCount = 8; // Number of skeleton rows to show

  return (
    <Stack spacing={1} sx={{ p: 2 }}>
      {/* Header skeleton */}
      <Skeleton variant="rectangular" height={32} />

      {/* Rows skeletons */}
      {Array(rowCount)
        .fill(0)
        .map((_, rowIndex) => (
          <Skeleton key={`row-${rowIndex}`} variant="rectangular" height={32} />
        ))}
    </Stack>
  );
};

export const AllocationTable: React.FC<Props> = ({ allocationTableData, allocationTableLoading }) => {
  const formatNumeric = useFormatNumericHook();

  return (
    <Stack
      sx={{
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "sm"
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          paddingBottom: "10px",
          boxShadow: "none",
          flexGrow: 1,
          overflowX: "auto",
          border: "1px solid #DBE8F5",
          borderRadius: "8px",
          "& .MuiPaper-root": {
            borderRadius: 2
          },
          "& .MuiTable-root": {
            borderCollapse: "separate",
            borderSpacing: 0,
            tableLayout: "fixed",
            minWidth: FIRST_COLUMN_WIDTH + DEFAULT_COLUMN_WIDTH * (allocationTableData?.columns?.length ?? 0 - 1)
          }
        }}
      >
        {allocationTableLoading ? (
          <LoadingSkeleton />
        ) : (
          allocationTableData && (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {allocationTableData?.columns.map((column, index) => (
                    <TableCell
                      key={column.key}
                      align={index === 0 ? "left" : "center"}
                      sx={{
                        backgroundColor: "#F6F8FC",
                        fontWeight: 600,
                        borderBottom: "1px solid #DBE8F5",
                        borderRight: "1px solid #DBE8F5",
                        color: "#1A1B1D",
                        width: index === 0 ? FIRST_COLUMN_WIDTH : DEFAULT_COLUMN_WIDTH,
                        minWidth: index === 0 ? FIRST_COLUMN_WIDTH : DEFAULT_COLUMN_WIDTH,
                        ...(index === 0 && firstColumnHeaderStyles)
                      }}
                    >
                      {column.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {allocationTableData?.rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {allocationTableData.columns.map((column, cellIndex) => (
                      <TableCell
                        key={column.key}
                        align={"left"}
                        sx={{
                          borderBottom: "1px solid #DBE8F5",
                          borderRight: "1px solid #DBE8F5",
                          width: cellIndex === 0 ? FIRST_COLUMN_WIDTH : DEFAULT_COLUMN_WIDTH,
                          minWidth: cellIndex === 0 ? FIRST_COLUMN_WIDTH : DEFAULT_COLUMN_WIDTH,
                          color: cellIndex === 0 ? "inherit" : "#92A7BC",
                          ...(cellIndex === 0 && {
                            ...firstColumnStyles,
                            fontWeight: 500
                          })
                        }}
                      >
                        {typeof row[column.key] === "number"
                          ? formatNumeric({ value: row[column.key], format: {} })
                          : row[column.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        )}
      </TableContainer>
    </Stack>
  );
};
